import { Button, Col, Form, InputNumber, Row, Spin, Select, Upload } from 'antd';
import React, { useState, useEffect } from 'react';
import CountUp from 'react-countup';
import { RouteComponentProps } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { rechargeByBankAccount, soldeTotalByEcoleSalaire } from './appro.reducer';
import { getEcole } from '../ecole/ecole.reducer';
import { getConfigurations } from '../appConfig/appConfig.reducer';
import { DownloadOutlined } from '@ant-design/icons';

export const DepotCompteBancaire = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const soldeTotal = useAppSelector(state => state.approvisonnement.soldeTotal);
  const loading = useAppSelector(state => state.approvisonnement.loading);
  const allConfig = useAppSelector(state => state.appConfig.entities);
  const [fileList, setFileList] = useState<any[]>([]);
  const [fieldValue, setFieldValue] = useState<string | undefined>(undefined);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(soldeTotalByEcoleSalaire({ ecoleId: props.match.params.ecoleid }));
    const fetchInitialData = async () => {
      await dispatch(getConfigurations());
      setIsConfigLoaded(true);
    };
    fetchInitialData();
  }, [dispatch, props.match.params.ecoleid]);

  const filteredConfig = allConfig.filter(config => config.configKey === 'ECOBANK');
  const initialValue = filteredConfig.length > 0 ? `${filteredConfig[0].configKey} - ${filteredConfig[0].value}` : undefined;

  useEffect(() => {
    if (initialValue) {
      setFieldValue(initialValue);
    }
  }, [initialValue]);

  const handleFileChange = (info: any) => {
    setFileList(info.fileList);
  };

  const saveRecharge = () => {
    const values = form.getFieldsValue();
    const formData = new FormData();
    formData.append('montant', values.montant);
    formData.append('confirmMontant', values.confirmMontant);
    formData.append('depotsBanque', values.depotsBanque);
    formData.append('anneeScolaireId', props.match.params.anneeScolaireId);
    if (fileList.length > 0) {
      formData.append('preuveId', fileList[0].originFileObj);
    }
    formData.append('ecoleId', props.match.params.ecoleid);

    dispatch(rechargeByBankAccount(formData)).then(() => {
      setFileList([]);
      form.resetFields();
      props.history.push(`/approvisonnements/${props.match.params.ecoleid}/${props.match.params.anneeScolaireId}`);
    });
  };

  const renderOptions = () => (
    <>
      {filteredConfig.map(filter => (
        <Select.Option key={filter.id} value={filter.id}>
          {filter.configKey} - {filter.value}
        </Select.Option>
      ))}
    </>
  );

  return (
    <div>
      <div style={{ textAlign: 'center', marginTop: '10px' }}>
        <h4 style={{ fontWeight: 'bold' }}>Transfert des Fonds</h4>
        <p style={{ fontSize: '18px', marginTop: '20px' }}>
          Une fois que vous avez effectué un dépôt de fonds sur un compte bancaire, <br /> veuillez nous en informer en renseignant ce
          formulaire ci-dessous. La preuve préférée est un avis de débit / avis Swift.
        </p>
      </div>

      <Row className="p-2 justify-content-center" style={{ marginBottom: '20px' }}>
        {loading ? (
          <Spin tip="Chargement..." />
        ) : (
          <h4 style={{ color: '000A', marginTop: '30px', fontWeight: 'bold' }}>
            Solde compte Salaire:{' '}
            <strong>
              <CountUp end={soldeTotal} separator="." /> FCFA
            </strong>
          </h4>
        )}
      </Row>

      <Row justify="center" style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={20} md={16} lg={12}>
          <Form name="approvisonnement" onFinish={saveRecharge} form={form} layout="vertical">
            <Form.Item
              name="montant"
              label={<span>Montant</span>}
              required
              rules={[
                {
                  type: 'number',
                  min: 0,
                },
                { required: true, message: 'Veuillez saisir le montant' },
              ]}
              style={{ marginBottom: '20px' }}
            >
              <InputNumber
                placeholder="CFA"
                min={0}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '0 10px',
                  height: '45px',
                  lineHeight: '45px',
                }}
                className="form-control"
                controls={false}
              />
            </Form.Item>

            <Form.Item
              required
              name="confirmMontant"
              label={<span>Confirmez le montant</span>}
              dependencies={['montant']}
              rules={[
                {
                  validator(_, value) {
                    if (!value || value === form.getFieldValue('montant')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Le montant saisi et le montant de confirmation ne correspondent pas.'));
                  },
                },
                { required: true, message: 'Veuillez confirmer le montant' },
              ]}
              style={{ marginBottom: '20px' }}
            >
              <InputNumber
                placeholder="CFA"
                min={0}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '0 10px',
                  height: '45px',
                  lineHeight: '45px',
                }}
                className="form-control"
              />
            </Form.Item>
            {isConfigLoaded && (
              <Form.Item
                required
                name="depotsBanque"
                initialValue={initialValue}
                label={<span>Sélectionnez un dépôt banque</span>}
                rules={[{ required: true, message: 'Veuillez sélectionner un dépôt banque' }]}
                style={{ marginBottom: '20px' }}
              >
                <Select
                  placeholder="Sélectionnez un dépôt banque"
                  value={fieldValue}
                  style={{
                    width: '100%',
                    height: '45px',
                    borderRadius: '8px',
                    border: '2px solid #F28E19',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  bordered={false}
                  onChange={value => {
                    setFieldValue(value);
                  }}
                >
                  {renderOptions()}
                </Select>
              </Form.Item>
            )}
            <Form.Item
              required
              name="preuveId"
              rules={[{ required: true, message: 'Veuillez télécharger une preuve de paiement' }]}
              style={{ border: '1px dashed #ccc', padding: '20px' }}
            >
              <Upload fileList={fileList} beforeUpload={() => false} maxCount={1} accept=".pdf,.jpg,.png" onChange={handleFileChange}>
                <DownloadOutlined style={{ fontSize: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
                <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  Preuve de paiement (ex: avis de débit, avis Swift) <br />
                  <span style={{ fontSize: '12px' }}>PDF, JPG, PNG</span>
                </span>
              </Upload>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  backgroundColor: '#F28E19',
                  color: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #F28E19',
                  width: '100%',
                  height: '45px',
                }}
              >
                {loading ? (
                  <Spin />
                ) : (
                  <span
                    style={{
                      fontWeight: '600',
                      fontSize: '18px',
                    }}
                  >
                    Envoyer
                  </span>
                )}
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default DepotCompteBancaire;
